import { useApi, useLoad } from "@allergy123/api-client/react";
import { ApiError } from "@allergy123/api-client";
import type { AttemptResponse } from "@allergy123/contracts";
import { Button, Card, Status } from "@allergy123/ui";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { modulePath } from "../lib";
import { clearSubmissionKey, submissionKeyFor } from "../submission";

/** A knowledge check: one attempt per submission, remediation for missed items, never the answer key. */
export function CheckPage() {
  const { code = "" } = useParams();
  const api = useApi();
  const data = useLoad(async (a) => {
    const [cat, check] = await Promise.all([a.catalog(), a.check(code)]);
    return { cat, check };
  }, [code]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<AttemptResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (data.state === "error") return <Status kind="error" label={`Couldn't load this check (${data.error?.message})`} />;
  if (!data.data) return <p className="text-ink-secondary">Loading…</p>;
  const { cat, check } = data.data;
  const unanswered = check.items.filter((i) => !answers[i.id]).length;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.learning.attempt(check.code, answers, submissionKeyFor(check.code, answers));
      // The attempt is recorded; a further submission is a new one and earns its own attempt.
      clearSubmissionKey(check.code);
      setResult(res);
    } catch (err) {
      const messages: Record<string, string> = {
        not_enrolled: "Enroll in the module before taking its check.",
        attempts_exhausted: "You've used every attempt for this check. Ask your Allergy Program Manager or Allergy 123 support about remediation.",
        check_not_authored: "This check hasn't been written yet.",
        idempotency_conflict: "This submission changed while it was being sent. Reload the check before trying again.",
      };
      // A 4xx is the server rejecting this submission outright, so the key has done its job. A 5xx or a
      // transport failure leaves the outcome unknown: keep the key so a retry is recognised as the same
      // submission rather than counted as another attempt.
      if (err instanceof ApiError && err.status < 500) clearSubmissionKey(check.code);
      setError(err instanceof ApiError ? (messages[err.code] ?? `Couldn't submit (${err.code}).`) : "Couldn't submit.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p className="mb-2 text-small text-ink-secondary">
        <Link to="/training">Training</Link> · <Link to={modulePath(cat, check.moduleCode)}>{cat.modules.find((m) => m.code === check.moduleCode)?.title}</Link>
      </p>
      <h1 className="mb-1 text-h1 font-semibold text-anchor">{check.title}</h1>
      <p className="mb-8 max-w-[68ch] text-ink-secondary">
        Pass mark {check.passingRule.minPercent}%{check.passingRule.allCritical ? ", and every critical item must be right" : ""}. Missed items come back with the reason, so you can review and try again.
      </p>
      {check.status !== "authored" && check.status !== "approved" && (
        <div className="mb-6">
          <Status kind="warning" label={check.status === "unauthored" ? "This check has not been written yet" : `Check status: ${check.status}`} />
        </div>
      )}

      {check.items.length === 0 ? (
        <Card>
          <p>Items for this check are still being written. The module's classes remain available.</p>
        </Card>
      ) : result ? (
        <Card as="section" aria-live="polite">
          <div className="mb-4">
            <Status kind={result.result.passed ? "success" : "warning"} label={result.result.passed ? `Passed — ${result.result.scorePercent}%` : `Not yet — ${result.result.scorePercent}%`} />
          </div>
          {!result.result.passed && !result.result.criticalItemsPassed && <p className="mb-4">A critical item was missed. Review the notes below; a pass needs every critical item right.</p>}
          {result.remediation.length > 0 && (
            <ol className="grid gap-4">
              {result.remediation.map((r) => {
                const item = check.items.find((i) => i.id === r.itemId);
                return (
                  <li key={r.itemId} className="border-l-2 border-rule-strong pl-4">
                    <p className="font-medium">{item?.prompt}</p>
                    <p className="text-ink-secondary">{r.remediation}</p>
                  </li>
                );
              })}
            </ol>
          )}
          <div className="mt-6 flex flex-wrap gap-4">
            {!result.result.passed && (
              <Button
                onClick={() => {
                  setResult(null);
                  setAnswers({});
                }}
              >
                Try again
              </Button>
            )}
            <Button variant="secondary" surface="card" href={modulePath(cat, check.moduleCode)}>
              Back to the module
            </Button>
          </div>
        </Card>
      ) : (
        <form onSubmit={(e) => void submit(e)} className="grid gap-6">
          {check.items.map((item, n) => (
            <Card key={item.id} as="section">
              <fieldset>
                <legend className="mb-3 font-medium">
                  {n + 1}. {item.prompt}
                </legend>
                <div className="grid gap-2">
                  {item.options.map((o) => (
                    <label key={o.id} className="flex cursor-pointer items-start gap-3 rounded-sm border border-rule-subtle px-3 py-2 has-[:checked]:border-rule-strong has-[:checked]:bg-card-tinted">
                      <input type="radio" name={item.id} value={o.id} checked={answers[item.id] === o.id} onChange={() => setAnswers((a) => ({ ...a, [item.id]: o.id }))} className="mt-1" />
                      <span>{o.text}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </Card>
          ))}
          {error && <Status kind="error" label={error} />}
          <div className="flex items-center gap-4">
            <Button type="submit" disabled={busy || unanswered > 0}>
              {busy ? "Submitting…" : "Submit answers"}
            </Button>
            {unanswered > 0 && <span className="text-small text-ink-secondary">{unanswered} unanswered</span>}
          </div>
        </form>
      )}
    </>
  );
}
